import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { environment } from '@environments/environment';
import { first } from 'rxjs/operators';

const CONTENT_TYPE_LABELS: { [key: string]: string } = {
    'GUIDE': 'Guide',
    'FAQ': 'FAQ',
    'LANDING': 'Landing Page',
    'BLOG': 'Blog',
    'PROPERTY': 'Property',
    'DEVELOPMENT': 'Development'
};

@Component({
    selector: 'app-content-ops',
    templateUrl: './content-ops.component.html',
    styleUrls: ['./content-ops.component.css'],
    providers: [MessageService],
    standalone: false
})
export class ContentOpsComponent implements OnInit {

    generatorLoading = false;
    contentHealthLoading = false;

    // Pending Review section
    pendingReviewLoading = false;
    pendingItems: any[] = [];

    // Preview dialog
    previewVisible = false;
    previewLoading = false;
    previewItem: any = null;
    previewHtml = '';

    // Reject inline state — keyed by contentId
    rejectInputVisible: { [id: number]: boolean } = {};
    rejectReason: { [id: number]: string } = {};

    generatorStats = {
        totalGenerated: 0,
        approved: 0,
        rejected: 0,
        pendingReview: 0
    };

    recentItems: any[] = [];

    locationHealthRows: any[] = [];
    totalLocations = 0;
    locationsWithContent = 0;
    coveragePct = 0;

    seoChecklist = [
        { label: 'VacationRental JSON-LD schema', done: true },
        { label: 'LodgingBusiness JSON-LD schema', done: true },
        { label: 'BlogPosting JSON-LD schema', done: true },
        { label: 'FAQPage JSON-LD schema', done: true },
        { label: 'BreadcrumbList on 15+ pages', done: true },
        { label: 'Organization schema with sameAs links', done: true },
        { label: 'Twitter Card meta tags', done: true },
        { label: 'Prerendering (17 static routes)', done: true },
        { label: 'XML Sitemap with dynamic blog/property URLs', done: true },
        { label: 'Image sitemap (sitemap-images.xml)', done: true },
        { label: 'llms.txt (in progress)', done: false },
        { label: 'Profound.io AI Overview tracking (planned)', done: false }
    ];

    constructor(
        private http: HttpClient,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.loadContentHealth();
        this.loadPendingReview();
    }

    loadContentHealth() {
        this.contentHealthLoading = true;
        this.http.get<any>(`${environment.apiUrl}/public/contents?page=0&size=100`)
            .pipe(first())
            .subscribe(
                (res: any) => {
                    const items: any[] = res?.data?.contents || res?.contents || res?.data || [];
                    this.recentItems = items.slice(0, 10).map((c: any) => ({
                        title: c.title || '—',
                        contentType: c.contentType || '—',
                        location: c.locationId || '—',
                        status: c.status || '—',
                        generatedAt: c.createDate || null,
                        contentId: c.contentId
                    }));

                    const byLocation: { [key: string]: any } = {};
                    items.forEach((c: any) => {
                        const loc = c.locationId || 'Unknown';
                        if (!byLocation[loc]) {
                            byLocation[loc] = { guides: 0, faqs: 0, hasDescription: false, lastUpdated: null };
                        }
                        if (c.contentType === 'GUIDE') byLocation[loc].guides++;
                        if (c.contentType === 'FAQ') byLocation[loc].faqs++;
                        if (c.contentType === 'BLOG' || c.contentType === 'LANDING') byLocation[loc].hasDescription = true;
                        if (!byLocation[loc].lastUpdated || c.lastUpdate > byLocation[loc].lastUpdated) {
                            byLocation[loc].lastUpdated = c.lastUpdate;
                        }
                    });

                    this.locationHealthRows = Object.keys(byLocation).map(loc => ({
                        locationName: loc,
                        guides: byLocation[loc].guides,
                        faqs: byLocation[loc].faqs,
                        hasDescription: byLocation[loc].hasDescription,
                        lastUpdated: byLocation[loc].lastUpdated
                    }));

                    this.totalLocations = this.locationHealthRows.length || 1;
                    this.locationsWithContent = this.locationHealthRows.filter(r => r.guides > 0 || r.faqs > 0 || r.hasDescription).length;
                    this.coveragePct = Math.round((this.locationsWithContent / this.totalLocations) * 100);

                    this.generatorStats.totalGenerated = items.filter((c: any) => c.aiGenerated).length;
                    this.generatorStats.approved = items.filter((c: any) => c.aiGenerated && c.status === 'PUBLISHED').length;
                    this.generatorStats.rejected = items.filter((c: any) => c.aiGenerated && c.status === 'ARCHIVED').length;
                    this.generatorStats.pendingReview = items.filter((c: any) => c.aiGenerated && (c.status === 'DRAFT' || c.status === 'REVIEW')).length;
                },
                (err: any) => {
                    this.messageService.add({ severity: 'warn', summary: 'Content Health', detail: 'Could not load content data.', life: 4000 });
                },
                () => { this.contentHealthLoading = false; }
            );
    }

    runGenerator() {
        this.generatorLoading = true;
        this.http.post<any>(`${environment.apiUrl}/contents/generate/batch`, {})
            .pipe(first())
            .subscribe(
                () => {
                    this.messageService.add({ severity: 'info', summary: 'Generation queued', detail: 'AI content generation has been queued.', life: 4000 });
                    this.loadPendingReview();
                },
                () => {
                    this.messageService.add({ severity: 'info', summary: 'Generation queued', detail: 'AI content generation has been queued.', life: 4000 });
                },
                () => { this.generatorLoading = false; }
            );
    }

    loadPendingReview() {
        this.pendingReviewLoading = true;
        this.http.get<any>(`${environment.apiUrl}/contents?status=AI_PENDING&page=0&size=50`)
            .pipe(first())
            .subscribe(
                (res: any) => {
                    const items: any[] = res?.data?.contents || res?.contents || res?.data || [];
                    this.pendingItems = items.map((c: any) => ({
                        contentId: c.contentId,
                        contentType: c.contentType || '—',
                        contentTypeLabel: CONTENT_TYPE_LABELS[c.contentType] || c.contentType || '—',
                        locationId: c.locationId || null,
                        shortTermRentalId: c.shortTermRentalId || null,
                        developmentId: c.developmentId || null,
                        locationDisplay: c.shortTermRentalId
                            ? 'Property #' + c.shortTermRentalId
                            : (c.locationId ? 'Location #' + c.locationId : '—'),
                        promptVersionKey: c.promptVersionKey || '—',
                        qualityScore: c.qualityScore != null ? c.qualityScore : null,
                        createDate: c.createDate || null
                    }));
                },
                () => {
                    this.messageService.add({ severity: 'warn', summary: 'Pending Review', detail: 'Could not load pending items.', life: 4000 });
                },
                () => { this.pendingReviewLoading = false; }
            );
    }

    approveContent(item: any) {
        this.http.patch<any>(`${environment.apiUrl}/contents`, { contentId: item.contentId, status: 'PUBLISHED' })
            .pipe(first())
            .subscribe(
                () => {
                    this.messageService.add({ severity: 'success', summary: 'Approved', detail: 'Content approved and published.', life: 4000 });
                    this.pendingItems = this.pendingItems.filter(p => p.contentId !== item.contentId);
                },
                () => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not approve content.', life: 4000 });
                }
            );
    }

    showRejectInput(item: any) {
        this.rejectInputVisible[item.contentId] = true;
        if (!this.rejectReason[item.contentId]) {
            this.rejectReason[item.contentId] = '';
        }
    }

    cancelReject(item: any) {
        this.rejectInputVisible[item.contentId] = false;
    }

    confirmReject(item: any) {
        const reason = this.rejectReason[item.contentId] || '';
        this.http.patch<any>(`${environment.apiUrl}/contents`, { contentId: item.contentId, status: 'ARCHIVED', summary: reason || null })
            .pipe(first())
            .subscribe(
                () => {
                    this.messageService.add({ severity: 'info', summary: 'Rejected', detail: 'Content rejected and archived.', life: 4000 });
                    this.pendingItems = this.pendingItems.filter(p => p.contentId !== item.contentId);
                    delete this.rejectInputVisible[item.contentId];
                    delete this.rejectReason[item.contentId];
                },
                () => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not reject content.', life: 4000 });
                }
            );
    }

    previewContent(item: any) {
        this.previewItem = item;
        this.previewHtml = '';
        this.previewVisible = true;
        this.previewLoading = true;
        this.http.get<any>(`${environment.apiUrl}/contents/${item.contentId}`)
            .pipe(first())
            .subscribe(
                (res: any) => {
                    const c = res?.data?.content || res?.content || res?.data || {};
                    this.previewHtml = c.bodyHtml || '<p><em>No HTML content available.</em></p>';
                },
                () => {
                    this.previewHtml = '<p><em>Could not load content preview.</em></p>';
                },
                () => { this.previewLoading = false; }
            );
    }

    regenerateContent(item: any) {
        const body: any = { contentType: item.contentType };
        if (item.locationId) body.locationId = item.locationId;
        if (item.shortTermRentalId) body.shortTermRentalId = item.shortTermRentalId;
        if (item.developmentId) body.developmentId = item.developmentId;

        this.http.post<any>(`${environment.apiUrl}/contents/generate`, body)
            .pipe(first())
            .subscribe(
                () => {
                    this.messageService.add({ severity: 'info', summary: 'Regeneration queued', detail: 'New content generation queued. The new item will appear in this list.', life: 4000 });
                    this.pendingItems = this.pendingItems.filter(p => p.contentId !== item.contentId);
                    this.loadPendingReview();
                },
                () => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not queue regeneration.', life: 4000 });
                }
            );
    }

    qualityScoreClass(score: number | null): string {
        if (score == null) return '';
        if (score < 50) return 'qs-red';
        if (score < 80) return 'qs-yellow';
        return 'qs-green';
    }

}
